/**
 * One company's artwork, from screen-library-detail in docs/prototype.html.
 *
 * GROUPED BY EVENT, not by year, which is what the prototype drew. A client
 * running two conferences in a year would otherwise get both under one heading
 * with no way to tell which artwork belonged to which — and the event is what
 * we actually store, so grouping by year would be a lossy summary of it.
 *
 * The exports are real now. They are generated once at ingest, out of process,
 * and this page only ever reads the stored results; see
 * server/src/lib/renditions.ts. Every one of them is optional, so EPS, a failed
 * render, and anything the backfill has not reached all fall back to a format
 * badge rather than a broken image.
 */

import { t } from '../lib/copy';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { formatLabel, isVector } from '../lib/fileFormat';
import { ORIENTATIONS, TREATMENTS } from '../lib/variations';
import { T } from '../lib/copyEdit.tsx';

interface Asset {
  assetId: string;
  orientation: string | null;
  treatment: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  uploadSource: 'sponsor' | 'staff';
  /** Who added it, when staff did. Null when the sponsor uploaded it. */
  uploadedByName: string | null;
  /** Which generated copies exist. Names only. */
  renditions: string[];
  at: string;
  previewable: boolean;
  /** The newest version of THIS orientation and treatment. Per label, not per
   *  company: a horizontal and a vertical are both current. */
  isCurrent: boolean;
}

/**
 * One orientation and treatment, and every file filed under it.
 *
 * What the page leads with. The event history below answers "what did they use
 * in 2024"; this answers "what do they use", which is what people actually
 * come here for.
 */
interface Version {
  key: string;
  label: string;
  current: Asset;
  /** What this version replaced, newest first. Collapsed by default. */
  older: Asset[];
}

interface EventGroup {
  eventId: string;
  eventName: string;
  startsOn: string | null;
  tierTitle: string | null;
  assets: Asset[];
}

interface Company {
  sponsorId: string;
  companyName: string;
  website: string | null;
  appIconData: string | null;
  inLibrarySince: string | null;
  eventCount: number;
  /** The most recent sponsorship's tier. A company has one per event, and
   *  only the latest means anything on a record like this. */
  tierTitle: string | null;
  /** The most recent sponsorship's primary contact, which is what somebody
   *  opening a library record most often actually wants. */
  contact: { name: string; email: string } | null;
  /** Which asset should lead the page. Chosen server-side by the same helper
   *  that picks the grid card's cover; null when nothing is renderable. */
  coverAssetId: string | null;
}

interface Target {
  eventSponsorId: string;
  eventName: string;
  slots: { id: string; label: string; filled: boolean }[];
}

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A version's name.
 *
 * Orientation and treatment when the asset has them, which is everything that
 * arrived against a slot on an event. Anything filed straight against the
 * company has neither — the upload has nowhere to ask — so its filename stands
 * in for a label nobody ever captured. That is why this string is sometimes
 * sixty characters of hyphenated noise.
 */
function label(asset: Asset): string {
  return [asset.orientation, asset.treatment].filter(Boolean).join(' · ') || asset.filename;
}

/** Whether this version has a real name, or is falling back to its filename.
 *  Either axis on its own counts: "Horizontal" with no treatment stated is a
 *  partial label, not an absent one. */
function labelled(asset: Asset): boolean {
  return Boolean(asset.orientation ?? asset.treatment);
}

/**
 * Trims a long name from the MIDDLE, never the end.
 *
 * Filenames differ where they finish: "-v2", "-final", "-2026", the extension.
 * An end ellipsis removes exactly the part that tells two versions of the same
 * artwork apart, leaving a column of identical-looking rows. Taken from both
 * ends instead, "sunbelt-network-horizontal-fullcolor-2026-final-v3.svg" reads
 * as "sunbelt-network-horizo…2026-final-v3.svg" and stays distinguishable.
 *
 * A real label is far shorter than the cap, so this leaves those untouched.
 */
function shorten(name: string, max = 38): string {
  if (name.length <= max) return name;
  const tail = 17;
  return `${name.slice(0, max - tail - 1)}…${name.slice(-tail)}`;
}

/** Reversed artwork is white, so a white stage renders it as nothing at all.
 *  The prototype draws exactly these on a dark ground for the same reason. */
const REVERSED = /revers|all[-\s]?white|knock[-\s]?out|white/i;

function isReversed(asset: Asset | undefined): boolean {
  return Boolean(asset?.treatment && REVERSED.test(asset.treatment));
}

/** The image for an asset at a given size, or null when nothing can be drawn
 *  and the caller should fall back to a format badge. */
function assetSrc(asset: Asset, base: string, name: 'thumb' | 'preview'): string | null {
  if (asset.renditions.includes(name)) return `${base}/assets/${asset.assetId}/rendition/${name}`;
  /* An asset the backfill has not reached. The original is servable directly
     for the three formats a browser draws unaided. */
  return asset.previewable ? `${base}/assets/${asset.assetId}/preview` : null;
}

function monthYear(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

/** The two the export buttons offer, in the order the prototype lists them. */
const EXPORTS = [
  { name: 'png', label: 'PNG · transparent' },
  { name: 'jpeg', label: 'JPEG · white' },
] as const;

export default function LibraryDetail() {
  const { clientId, sponsorId } = useParams();
  const base = `/api/clients/${clientId}/library`;

  const [company, setCompany] = useState<Company | null>(null);
  const [history, setHistory] = useState<EventGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reusing, setReusing] = useState<Asset | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [unlabelled, setUnlabelled] = useState<Asset[]>([]);
  /** The version whose label is being set, or null. */
  const [labelling, setLabelling] = useState<Asset | null>(null);
  const [onDark, setOnDark] = useState(false);
  /**
   * Which version the stage is showing.
   *
   * THIS WAS DERIVED AND HAD NO SETTER, which made the whole left column a
   * dead end: the page picked a cover server-side and there was no way to look
   * at any of the other versions large. Null means "use the cover", so the
   * server's choice still leads until somebody picks something else.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  /** What the next uploaded version will be labelled. */
  const [newOrientation, setNewOrientation] = useState('');
  const [newTreatment, setNewTreatment] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${base}/${sponsorId}`);
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as {
        company: Company;
        versions: Version[];
        unlabelled: Asset[];
        history: EventGroup[];
      };
      setCompany(body.company);
      setVersions(body.versions ?? []);
      setUnlabelled(body.unlabelled ?? []);
      setHistory(body.history);
    } catch {
      setError('Could not load this sponsor.');
    }
  }, [base, sponsorId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="auth-error">{error}</p>;
  if (!company) return <div className="app-loading" aria-busy="true" />;

  /* The server chooses which artwork leads, using the same helper that picks
     the grid card's cover, so the two screens cannot disagree. This used to be
     `history[0].assets[0]` — whatever sorted first — which meant a company
     whose reversed all-white version happened to lead showed a white logo on a
     white ground and looked as though it had no artwork at all.

     Falls back to the old behaviour when nothing is renderable, so the page
     still has something to describe. */
  const allAssets = history.flatMap((group) => group.assets);
  const cover = allAssets.find((a) => a.assetId === company.coverAssetId) ?? history[0]?.assets[0];
  const current = allAssets.find((a) => a.assetId === selectedId) ?? cover;

  /* Reversed artwork is white. Shown on the default white stage it is
     invisible, which is exactly how a company with good artwork came to look
     as though it had none. The ground follows the artwork unless the reviewer
     has said otherwise. */
  const dark = onDark || isReversed(current);

  /**
   * Files a new version against the company.
   *
   * IT SENDS THE LABEL, which is what stops this problem being recreated. The
   * endpoint has always accepted an orientation and a treatment; the form never
   * asked, so every version filed this way arrived unnamed and the library fell
   * back to printing its filename. The picker below chooses them before the
   * file dialog opens.
   */
  async function uploadVersion(chosen: File) {
    setUploading(true);
    setUploadError(null);
    const form = new FormData();
    form.append('file', chosen);
    if (newOrientation) form.append('orientation', newOrientation);
    if (newTreatment) form.append('treatment', newTreatment);
    try {
      const res = await fetch(`${base}/${sponsorId}/upload`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setUploadError(body.error ?? 'That did not upload. Please try again.');
        return;
      }
      await load();
    } catch {
      setUploadError('That did not upload. Check your connection and try again.');
    } finally {
      setUploading(false);
      if (file.current) file.current.value = '';
    }
  }

  return (
    <>
      <Link className="back" to={`/c/${clientId}/library`}>
        <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m15 18-6-6 6-6" />
        </svg>
        <T k="library.title" d="Logo Library" />
      </Link>

      <div className="ehead">
        <div>
          <h1 className="title">{company.companyName}</h1>
          <div className="emeta">
            {company.tierTitle && <span>{company.tierTitle}</span>}
            {company.website && <span>{company.website}</span>}
            {company.inLibrarySince && (
              <span>In library since {monthYear(company.inLibrarySince)}</span>
            )}
            <span>
              Used in {company.eventCount} event{company.eventCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>
        {/* Both carry their glyph, like every other action button in the app.
            They were the two that did not, which made this header read as a
            different kind of control from the same pair on Event Setup. */}
        <div className="pactions">
          <a className="btn-compact btn-compact--quiet" href={`${base}/${sponsorId}/download`}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5M12 15V3" />
            </svg>
            <T k="common.action.downloadAll" d="Download All" />
          </a>
          {/* Promoted to the header, where the prototype has it. The per-asset
              Reuse below is more capable and stays; this is the common case of
              reusing the record's current artwork, which otherwise meant
              scrolling to find the right row first. */}
          {cover && (
            <button
              type="button"
              className="btn-compact"
              onClick={() => setReusing(current ?? cover)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-9-9c2.5 0 4.8 1 6.5 2.6" />
                <path d="M21 3v6h-6" />
              </svg>
              <T k="libraryDetail.action.reuse" d="Reuse in Event" />
            </button>
          )}
        </div>
      </div>

      {labelling && (
        <Sheet onClose={() => setLabelling(null)}>
          <LabelPanel
            base={base}
            asset={labelling}
            onCancel={() => setLabelling(null)}
            onDone={() => {
              setLabelling(null);
              void load();
            }}
          />
        </Sheet>
      )}

      {reusing && (
        <Sheet onClose={() => setReusing(null)}>
          <ReusePanel
            base={base}
            sponsorId={sponsorId ?? ''}
            asset={reusing}
            onCancel={() => setReusing(null)}
            onDone={() => {
              setReusing(null);
              void load();
            }}
          />
        </Sheet>
      )}

      <div className="sd-grid">
        <div>
          {current && (
            <>
              <Stage asset={current} base={base} dark={dark} />

              <div className="rv-openbar">
                <div className="chips">
                  <button
                    type="button"
                    className={`chip${dark ? '' : ' on'}`}
                    aria-pressed={!dark}
                    onClick={() => setOnDark(false)}
                  >
                    <T k="common.stage.onWhite" d="On White" />
                  </button>
                  <button
                    type="button"
                    className={`chip${dark ? ' on' : ''}`}
                    aria-pressed={dark}
                    onClick={() => setOnDark(true)}
                  >
                    <T k="common.stage.onDark" d="On Dark" />
                  </button>
                </div>
                <span className="csub">{label(current)}</span>
              </div>

              {/* THE THUMB ROW, without which the stage was a dead end: the
                  server picked a cover and no other version could be looked at
                  large. */}
              {allAssets.length > 1 && (
                <div className="ld-thumbs">
                  {allAssets.map((asset) => (
                    <button
                      key={asset.assetId}
                      type="button"
                      className={`ld-thumb${asset.assetId === current.assetId ? ' on' : ''}${
                        isReversed(asset) ? ' is-dark' : ''
                      }`}
                      aria-pressed={asset.assetId === current.assetId}
                      onClick={() => setSelectedId(asset.assetId)}
                    >
                      <AssetImage asset={asset} base={base} size="thumb" />
                      <span className="ld-thumb-name" title={label(asset)}>
                        {shorten(label(asset), 22)}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Download and export. The generated formats appear only when
                  they exist, so a version with none offers its original alone
                  rather than two buttons that would 404. */}
              <div className="panel ld-exports">
                <div className="panel-head">
                  <h3><T k="libraryDetail.exports.title" d="Download &amp; export" /></h3>
                </div>
                <div className="panel-body">
                  <div className="rv-exports">
                    <a
                      className="btn-compact btn-compact--quiet"
                      href={`${base}/assets/${current.assetId}/file`}
                    >
                      {isVector(current.mimeType) ? 'Vector · original' : 'Original'}
                    </a>
                    {EXPORTS.filter((e) => current.renditions.includes(e.name)).map((e) => (
                      <a
                        key={e.name}
                        className="btn-compact btn-compact--quiet"
                        href={`${base}/assets/${current.assetId}/rendition/${e.name}/download`}
                      >
                        {e.label}
                      </a>
                    ))}
                    <a
                      className="btn-compact btn-compact--quiet"
                      href={`${base}/${sponsorId}/download`}
                    >
                      <T k="libraryDetail.action.allZip" d="All (.zip)" />
                    </a>
                  </div>
                  {/* Said only where it is true. A version with no generated
                      copies makes no claim about them. */}
                  {current.renditions.length > 0 && (
                    <p className="fdesc" style={{ marginTop: 'var(--sp2)' }}>
                      <T k="libraryDetail.exports.hint" d="PNG and JPEG are generated from the original automatically." />
                    </p>
                  )}
                </div>
              </div>

              {/* T13: a rebrand a sponsor sends you directly. Until now the
                  only way artwork could enter the library was through a
                  sponsorship on an event, so "we have their new logo, can you
                  file it" had no answer. */}
              <div className="panel">
                <div className="panel-head">
                  <h3><T k="libraryDetail.upload.title" d="Upload new version" /></h3>
                </div>
                <div className="panel-body">
                  <p className="fdesc">
                    For a rebrand a sponsor sends you directly. It is filed against{' '}
                    {company.companyName} rather than any event, and can be reused into one from
                    here.
                  </p>

                  {/* ASKED BEFORE THE FILE, not after. Every version filed this
                      way used to arrive unnamed, because the endpoint accepted
                      a label and the form never offered one — which is the
                      whole reason the library was printing filenames. Chosen
                      first so the label is set by the time the upload fires. */}
                  <div className="frow2" style={{ marginTop: 'var(--sp3)' }}>
                    <div className="fg">
                      <label className="fl" htmlFor="new-orientation"><T k="common.field.orientation" d="Orientation" /></label>
                      <select
                        id="new-orientation"
                        className="fi"
                        value={newOrientation}
                        onChange={(e) => setNewOrientation(e.target.value)}
                      >
                        <option value="">{t('common.notSet', 'Not set')}</option>
                        {ORIENTATIONS.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <div className="fg">
                      <label className="fl" htmlFor="new-treatment"><T k="common.field.treatment" d="Colour treatment" /></label>
                      <select
                        id="new-treatment"
                        className="fi"
                        value={newTreatment}
                        onChange={(e) => setNewTreatment(e.target.value)}
                      >
                        <option value="">{t('common.notSet', 'Not set')}</option>
                        {TREATMENTS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <input
                    ref={file}
                    type="file"
                    className="portal-file"
                    accept=".svg,.eps,.ai,.pdf,.png,.jpg,.jpeg,image/svg+xml,application/pdf,application/postscript,image/png,image/jpeg"
                    onChange={(e) => {
                      const chosen = e.target.files?.[0];
                      if (chosen) void uploadVersion(chosen);
                    }}
                  />
                  <button
                    type="button"
                    className="btn-compact btn-compact--quiet"
                    style={{ marginTop: 'var(--sp3)' }}
                    disabled={uploading}
                    onClick={() => file.current?.click()}
                  >
                    {uploading ? 'Uploading…' : 'Choose a File'}
                  </button>
                  {uploadError && <p className="auth-error">{uploadError}</p>}
                </div>
              </div>
            </>
          )}

          {/* A RECORD WITH NO ARTWORK AT ALL. Everything above hangs off
              `current`, so without this the entire left column rendered
              nothing and the page came up as a details panel floating beside
              an empty half-screen. A company gets into the library by being
              added to an event, which happens before they send anything, so
              this is a normal state and not an error. */}
          {!current && (
            <div className="empty">
              <p className="empty-title"><T k="libraryDetail.empty.title" d="No artwork yet" /></p>
              <p className="empty-copy">
                {company.companyName} is in the library, but nothing has been collected for them.
                Artwork arrives when they upload against an event, or you can file a file against
                the company directly.
              </p>
              <input
                ref={file}
                type="file"
                className="portal-file"
                accept=".svg,.eps,.ai,.pdf,.png,.jpg,.jpeg,image/svg+xml,application/pdf,application/postscript,image/png,image/jpeg"
                onChange={(e) => {
                  const chosen = e.target.files?.[0];
                  if (chosen) void uploadVersion(chosen);
                }}
              />
              <button
                type="button"
                className="btn-compact"
                disabled={uploading}
                onClick={() => file.current?.click()}
              >
                {uploading ? 'Uploading…' : 'Upload a Version'}
              </button>
              {uploadError && <p className="auth-error">{uploadError}</p>}
            </div>
          )}
        </div>

        <div>
          {/* T14: the two things somebody opening a library record most often
              actually wants, and neither was anywhere on the page. */}
          <div className="panel">
            <div className="panel-head">
              <h3><T k="common.panel.details" d="Details" /></h3>
            </div>
            <div className="panel-body">
              <ul className="metalist">
                <li>
                  <span className="k"><T k="libraryDetail.field.tier" d="Tier" /></span>
                  <span className="v">{company.tierTitle ?? 'Not set'}</span>
                </li>
                <li>
                  <span className="k"><T k="libraryDetail.field.contact" d="Contact" /></span>
                  <span className="v">{company.contact?.name ?? 'None on file'}</span>
                </li>
                <li>
                  <span className="k"><T k="libraryDetail.field.email" d="Email" /></span>
                  <span className="v">
                    {company.contact ? (
                      <a href={`mailto:${company.contact.email}`}>{company.contact.email}</a>
                    ) : (
                      'None on file'
                    )}
                  </span>
                </li>
                <li>
                  <span className="k"><T k="libraryDetail.field.eventsUsed" d="Events used" /></span>
                  <span className="v">{company.eventCount}</span>
                </li>
                {company.website && (
                  <li>
                    <span className="k"><T k="common.field.website" d="Website" /></span>
                    <span className="v">{company.website}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* THE VERSIONS, which is what this page is for.
              One block per orientation and treatment, newest first, so a
              rebrand filed a minute ago is the first thing on the page and
              sits on top of the file it replaced. The event history below
              still records which conference used what. */}
          {versions.length > 0 && (
            <div className="panel">
              <div className="panel-head">
                <h3><T k="libraryDetail.versions.title" d="Versions" /></h3>
                <span className="hint">
                  <T k="libraryDetail.versions.hint" d="What they use now" />
                </span>
              </div>
              <div className="panel-body">
                {versions.map((version) => (
                  <VersionBlock
                    key={version.key}
                    version={version}
                    base={base}
                    onPick={setSelectedId}
                    onLabel={setLabelling}
                    onReuse={setReusing}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Artwork that cannot join a version because it was never labelled.
              Shown rather than hidden: it is a gap somebody can close, and the
              Label action is on the row. */}
          {unlabelled.length > 0 && (
            <div className="panel">
              <div className="panel-head">
                <h3><T k="libraryDetail.unlabelledGroup.title" d="Needs a label" /></h3>
                <span className="hint">
                  <T k="libraryDetail.unlabelledGroup.hint" d="Cannot be reused until labelled" />
                </span>
              </div>
              <div className="panel-body">
                <ul className="lassets">
                  {unlabelled.map((asset) => (
                    <AssetRow
                      key={asset.assetId}
                      asset={asset}
                      base={base}
                      onPick={setSelectedId}
                      onLabel={setLabelling}
                      onReuse={setReusing}
                    />
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Grouped by event, newest first. "What do they use now" is asked
              far more often than "what did they use in 2024". */}
          {history.map((group) => (
            <div className="panel" key={group.eventId}>
              <div className="panel-head">
                <h3>{group.eventName}</h3>
                {group.tierTitle && <span className="hint">{group.tierTitle}</span>}
              </div>
              <div className="panel-body">
                <ul className="lassets">
                  {group.assets.map((asset) => (
                    <AssetRow
                      key={asset.assetId}
                      asset={asset}
                      base={base}
                      onPick={setSelectedId}
                      onLabel={setLabelling}
                      onReuse={setReusing}
                    />
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * The large preview.
 *
 * THE GROUND FOLLOWS THE ARTWORK. An all-white reversed logo on a white stage
 * is invisible, which is how a company with seven good files came to look as
 * though it had none. Reversed artwork gets the dark ground by default and the
 * toggle still overrides it either way.
 */
function Stage({ asset, base, dark }: { asset: Asset; base: string; dark: boolean }) {
  const [failed, setFailed] = useState(false);
  const src = assetSrc(asset, base, 'preview');

  /* Reset when the selection changes, or a version whose preview failed would
     poison the stage for every version chosen after it. */
  useEffect(() => setFailed(false), [asset.assetId]);

  return (
    <div className={`rv-stage${dark ? ' on-dark' : ''}`}>
      {src && !failed ? (
        <img src={src} alt={label(asset)} onError={() => setFailed(true)} />
      ) : (
        <div className="rv-stage-type">{formatLabel(asset.mimeType, asset.filename)}</div>
      )}
    </div>
  );
}

/** An asset's artwork at thumb size, or its format badge. Used by the thumb
 *  row and by every row in the version history. */
function AssetImage({
  asset,
  base,
  size,
}: {
  asset: Asset;
  base: string;
  size: 'thumb' | 'preview';
}) {
  const [failed, setFailed] = useState(false);
  const src = assetSrc(asset, base, size);

  if (!src || failed) {
    return (
      <span className="lthumb-type" aria-hidden="true">
        {formatLabel(asset.mimeType, asset.filename)}
      </span>
    );
  }

  return <img src={src} alt="" onError={() => setFailed(true)} />;
}

/**
 * A panel that opens over the page instead of above it.
 *
 * WHY THIS EXISTS. LabelPanel and ReusePanel used to render inline at the top
 * of the document, below the header and above the two-column grid. The buttons
 * that open them are in the right-hand column, which on a company with any
 * history is well below the fold — so clicking Label scrolled nothing, showed
 * nothing, and left the editor sitting off-screen above you. Reported as
 * relabelling simply not working, which from the outside it was: the form was
 * real, saved correctly, and had never been seen.
 *
 * Closes on the scrim and on Escape. The scrim is a sibling rather than a
 * parent of the panel so a click inside the form cannot bubble out and dismiss
 * the thing being filled in.
 */
function Sheet({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    /* The page behind must not scroll while a sheet is open, or dismissing it
       returns you somewhere other than where you were. */
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="sheet-wrap" role="dialog" aria-modal="true">
      <button
        type="button"
        className="sheet-scrim"
        aria-label={t('common.action.close', 'Close')}
        onClick={onClose}
      />
      <div className="sheet">{children}</div>
    </div>
  );
}

/**
 * One orientation and treatment, with its current file and what it replaced.
 *
 * THE OLDER ONES ARE COLLAPSED, and that is a judgement rather than a saving.
 * The question this page answers is "what do they use"; the previous versions
 * are the audit trail behind that answer, wanted occasionally and never first.
 * Expanded they would triple the height of the page and bury the one row that
 * matters under the ones that do not.
 *
 * The count is on the summary, so "is there any history here" is answerable
 * without opening anything.
 */
function VersionBlock({
  version,
  base,
  onPick,
  onLabel,
  onReuse,
}: {
  version: Version;
  base: string;
  onPick: (assetId: string) => void;
  onLabel: (asset: Asset) => void;
  onReuse: (asset: Asset) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="lver">
      <ul className="lassets">
        <AssetRow
          asset={version.current}
          base={base}
          onPick={onPick}
          onLabel={onLabel}
          onReuse={onReuse}
        />
      </ul>

      {version.older.length > 0 && (
        <>
          <button
            type="button"
            className="lver-more"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open
              ? t('libraryDetail.versions.hide', 'Hide earlier versions')
              : version.older.length === 1
                ? t('libraryDetail.versions.showOne', '1 earlier version')
                : `${version.older.length} ${t('libraryDetail.versions.showMany', 'earlier versions')}`}
          </button>

          {open && (
            <ul className="lassets lassets--older">
              {version.older.map((asset) => (
                <AssetRow
                  key={asset.assetId}
                  asset={asset}
                  base={base}
                  onPick={onPick}
                  onLabel={onLabel}
                  onReuse={onReuse}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/**
 * One version, as a row.
 *
 * SHARED BY THE VERSION LIST AND THE EVENT HISTORY, which is the point: the
 * same file appears in both, and two copies of this markup would drift until
 * the same artwork described itself differently depending on where you were
 * looking at it.
 */
function AssetRow({
  asset,
  base,
  onPick,
  onLabel,
  onReuse,
}: {
  asset: Asset;
  base: string;
  onPick: (assetId: string) => void;
  onLabel: (asset: Asset) => void;
  onReuse: (asset: Asset) => void;
}) {
  return (
            <li>
              <button
                type="button"
                className="lthumb lthumb--pick"
                aria-label={`Show ${label(asset)}`}
                onClick={() => onPick(asset.assetId)}
              >
                <AssetImage asset={asset} base={base} size="thumb" />
              </button>

              {/* NAME ON ONE LINE, EVERYTHING ELSE ON THE NEXT. This
                  was a heading and two more lines below it — format and
                  size, then provenance and date — three lines of
                  near-identical grey text per row, which at seven
                  versions is a wall rather than a list. The details are
                  one sentence now, and "Current" is a proper badge
                  instead of a bespoke chip, so the only thing that
                  varies between rows is the thing you are looking
                  for.
                  Provenance stays in it: "uploaded by the sponsor" and
                  "uploaded by staff on their behalf" are different
                  facts, and the first is what settles a dispute about
                  what was actually sent. */}
              <span className="lassets-text">
                <b>
                  {/* THE LABEL LEADS, and the filename is no longer it.
                      A version named by its orientation and treatment
                      can be compared with every other version in the
                      app; one named after its file cannot. An
                      unlabelled version says so plainly and offers the
                      fix, rather than printing sixty characters of
                      hyphens and hoping. */}
                  {labelled(asset) ? (
                    <span className="lassets-name">{label(asset)}</span>
                  ) : (
                    <span className="lassets-name lassets-name--none"><T k="libraryDetail.unlabelled" d="Unlabelled" /></span>
                  )}
                  {asset.isCurrent && (
                    <span className="badge b-success">
                      <span className="d" />
                      <T k="libraryDetail.current" d="Current" />
                    </span>
                  )}
                </b>
                <span className="lassets-meta">
                  {/* The filename moves here, trimmed from the middle,
                      because filenames differ where they finish. It is
                      a fact about the file rather than a name for the
                      artwork, so it sits with the other facts. */}
                  <span title={asset.filename}>{shorten(asset.filename)}</span> ·{' '}
                  {formatLabel(asset.mimeType, asset.filename)} ·{' '}
                  {fileSize(asset.sizeBytes)}
                  <br />
                  {asset.uploadSource === 'staff'
                    ? `Uploaded by ${asset.uploadedByName ?? 'staff'} on the sponsor's behalf`
                    : 'Uploaded by the sponsor'}{' '}
                  · {monthYear(asset.at)}
                </span>
              </span>

              <span className="row-actions">
                <button
                  type="button"
                  className="cedit"
                  onClick={() => onLabel(asset)}
                >
                  <T k="libraryDetail.action.label" d="Label" />
                </button>
                <a className="cedit" href={`${base}/assets/${asset.assetId}/file`}>
                  <T k="common.action.download" d="Download" />
                </a>
                <button type="button" className="cedit" onClick={() => onReuse(asset)}>
                  <T k="libraryDetail.action.reuseShort" d="Reuse" />
                </button>
              </span>
            </li>
  );
}

/**
 * Names a version by its orientation and treatment.
 *
 * THE SAME TWO AXES AS EVERYTHING ELSE, from the shared list, because a label
 * is not a caption. It decides which requested version this artwork can answer
 * when somebody reuses it into an event, so a library version labelled
 * "Stacked" would match no slot at all. Free text would look tidier here and
 * be worth less everywhere else.
 *
 * THE FILENAME IS NOT EDITABLE, deliberately. It is what the sponsor actually
 * sent and what the download is called, and this system keeps their own
 * labelling separate from staff corrections so the difference stays auditable.
 * Renaming the file would make the record disagree with the copy they hold.
 *
 * Either axis can be left unset, which is what "Not set" is for: half a label
 * is better than a wrong one, and a version can legitimately be "the white
 * one" with no orientation worth stating.
 */
function LabelPanel({
  base,
  asset,
  onCancel,
  onDone,
}: {
  base: string;
  asset: Asset;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [orientation, setOrientation] = useState(asset.orientation ?? '');
  const [treatment, setTreatment] = useState(asset.treatment ?? '');
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save() {
    setWorking(true);
    setProblem(null);
    try {
      const res = await fetch(`${base}/assets/${asset.assetId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orientation, treatment }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setProblem(body.error ?? 'That did not save.');
        return;
      }
      onDone();
    } catch {
      setProblem('That did not save. Check your connection and try again.');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3><T k="libraryDetail.label.title" d="Label this version" /></h3>
        <span className="hint">{shorten(asset.filename, 44)}</span>
      </div>
      <div className="panel-body">
        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="label-orientation"><T k="common.field.orientation" d="Orientation" /></label>
            <select
              id="label-orientation"
              className="fi"
              value={orientation}
              onChange={(e) => setOrientation(e.target.value)}
            >
              <option value="">{t('common.notSet', 'Not set')}</option>
              {/* A value from before this list, or from another client's
                  vocabulary, is offered rather than silently dropped — picking
                  the dropdown open should never be what changes the label. */}
              {!ORIENTATIONS.includes(orientation) && orientation !== '' && (
                <option value={orientation}>{orientation}</option>
              )}
              {ORIENTATIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div className="fg">
            <label className="fl" htmlFor="label-treatment"><T k="common.field.treatment" d="Colour treatment" /></label>
            <select
              id="label-treatment"
              className="fi"
              value={treatment}
              onChange={(e) => setTreatment(e.target.value)}
            >
              <option value="">{t('common.notSet', 'Not set')}</option>
              {!TREATMENTS.includes(treatment) && treatment !== '' && (
                <option value={treatment}>{treatment}</option>
              )}
              {TREATMENTS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        <p className="fdesc">
          This is what the version is called everywhere, and it decides which requested version it
          can answer when you reuse it into an event. The file itself is not renamed.
        </p>

        {problem && <p className="auth-error">{problem}</p>}

        <div className="factions">
          <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
            <T k="common.action.cancel" d="Cancel" />
          </button>
          <button
            type="button"
            className="btn-compact"
            disabled={working}
            onClick={() => void save()}
          >
            {working ? 'Saving…' : 'Save Label'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Puts this artwork against a slot on an event the sponsor is already on.
 *
 * The point of the library: a returning sponsor whose logo has not changed
 * should not be chased for a file we already hold.
 *
 * It lands as PENDING rather than approved, and the panel says so. A company
 * can rebrand without telling its sponsees, and an approval carried forward
 * from two years ago would put stale artwork into print with nobody having
 * looked.
 */
function ReusePanel({
  base,
  sponsorId,
  asset,
  onCancel,
  onDone,
}: {
  base: string;
  sponsorId: string;
  asset: Asset;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [targets, setTargets] = useState<Target[] | null>(null);
  const [choice, setChoice] = useState('');
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    void fetch(`${base}/${sponsorId}/targets`)
      .then((r) => (r.ok ? r.json() : { targets: [] }))
      .then((b: { targets: Target[] }) => setTargets(b.targets))
      .catch(() => setTargets([]));
  }, [base, sponsorId]);

  async function submit() {
    const [eventSponsorId, variationId] = choice.split('|');
    setWorking(true);
    setProblem(null);

    try {
      const res = await fetch(`${base}/reuse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assetId: asset.assetId,
          eventSponsorId,
          eventVariationId: variationId || null,
        }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setProblem(body.error ?? 'That did not save.');
        return;
      }
      onDone();
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Reuse {label(asset)}</h3>
      </div>
      <div className="panel-body">
        {targets === null ? (
          <div className="app-loading" aria-busy="true" />
        ) : targets.length === 0 ? (
          <p className="fdesc" style={{ marginTop: 0 }}>
            This sponsor is not on any event that is still collecting. Add them to an event first,
            and the artwork will be here waiting.
          </p>
        ) : (
          <>
            <div className="fg">
              <label className="fl" htmlFor="reuse-target"><T k="libraryDetail.reuse.target" d="Use it for" /></label>
              <select
                id="reuse-target"
                className="fi"
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
              >
                <option value="">{t('libraryDetail.reuse.choose', 'Choose an event and version')}</option>
                {targets.map((target) => (
                  <optgroup key={target.eventSponsorId} label={target.eventName}>
                    {target.slots.map((slot) => (
                      <option
                        key={slot.id}
                        value={`${target.eventSponsorId}|${slot.id}`}
                      >
                        {slot.label}
                        {slot.filled ? ' (replaces what is there)' : ''}
                      </option>
                    ))}
                    <option value={`${target.eventSponsorId}|`}>{t('libraryDetail.reuse.extra', 'Add as an Extra')}</option>
                  </optgroup>
                ))}
              </select>
            </div>

            <p className="fdesc">
              It arrives as pending rather than approved, so you get a quick look first. A company
              can rebrand without telling anyone. Reminders for that version stop immediately.
            </p>

            {problem && <p className="auth-error">{problem}</p>}

            <div className="factions">
              <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
                <T k="common.action.cancel" d="Cancel" />
              </button>
              <button
                type="button"
                className="btn-compact"
                disabled={working || !choice}
                onClick={() => void submit()}
              >
                {working ? 'Adding…' : 'Use This Artwork'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
